import { useEffect, useState } from "react";
import { SealCheck, CheckCircle, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS, inr } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function QcQueue() {
  const [jobs, setJobs] = useState(null);
  const [comments, setComments] = useState({});

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const act = async (job, patch, msg) => {
    try {
      await api.put(`/jobs/${job.id}`, patch);
      toast.success(msg);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!jobs) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading QC queue…</div>;

  const queue = jobs.filter((j) => j.status === "admin_review");
  const awaiting = jobs.filter((j) => j.status === "draft_prepared");

  return (
    <div className="space-y-6" data-testid="qc-queue-page">
      <PageHeader overline="Valuation Jobs" title="Report QC Queue" subtitle="No report goes out without a senior sign-off" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Awaiting QC" value={queue.length} tone="text-indigo-600" testid="stat-qc" />
        <Stat label="Still drafting" value={awaiting.length} tone="text-amber-600" testid="stat-drafting" />
        <Stat label="Fees in queue" value={inr(queue.reduce((s, j) => s + (j.fee || 0), 0))} tone="text-accent" testid="stat-qc-fees" />
      </div>

      {queue.length === 0 ? (
        <EmptyState icon={SealCheck}>Nothing waiting for review. Move a job to <strong>Internal QC</strong> from the jobs board when its draft is ready.</EmptyState>
      ) : (
        <div className="space-y-3">
          {queue.map((j) => (
            <Card key={j.id} className="p-5 border-border" data-testid={`qc-card-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg text-primary">{j.applicant_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{j.reference} · {j.bank_name}{j.branch_name ? ` — ${j.branch_name}` : ""}</div>
                  <div className="text-sm text-muted-foreground mt-1">{j.property_type} · {j.property_address}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border shrink-0 ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
              </div>

              {j.qc_comments && (
                <div className="mt-3 rounded-md bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-900" data-testid={`qc-existing-${j.id}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 mb-1">Previous review comments</div>
                  {j.qc_comments}
                </div>
              )}

              <Textarea
                rows={2} className="mt-3 text-sm"
                placeholder="Review comments (measurements, comparables, photo quality, disclaimers…)"
                value={comments[j.id] ?? ""}
                onChange={(e) => setComments({ ...comments, [j.id]: e.target.value })}
                data-testid={`qc-comment-${j.id}`}
              />

              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => act(j, { status: "sent_to_client", qc_comments: comments[j.id] || j.qc_comments || "" }, "Approved & marked delivered")}
                  data-testid={`qc-approve-${j.id}`}>
                  <CheckCircle size={15} className="mr-1" /> Approve & Deliver
                </Button>
                <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => {
                    if (!comments[j.id]) { toast.error("Add a comment so the writer knows what to fix"); return; }
                    act(j, { status: "draft_prepared", qc_comments: comments[j.id] }, "Sent back for rework");
                  }}
                  data-testid={`qc-return-${j.id}`}>
                  <ArrowUUpLeft size={15} className="mr-1" /> Send Back for Rework
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground"
                  onClick={() => act(j, { qc_comments: comments[j.id] || "" }, "Comments saved")}
                  data-testid={`qc-save-${j.id}`}>
                  Save comments only
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {awaiting.length > 0 && (
        <Card className="p-5 border-border">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Coming up — reports still being drafted</div>
          <div className="space-y-1.5">
            {awaiting.map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-2 p-2 rounded bg-muted text-sm" data-testid={`qc-upcoming-${j.id}`}>
                <span className="text-primary truncate">{j.applicant_name} · {j.bank_name}</span>
                <span className="text-xs font-mono text-muted-foreground shrink-0">{j.reference}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
